import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import UserNotification from 'util/UserNotification';
import type { ColumnFilterData } from 'security-app/components/common/Filters/ColumnFilter.types';

import {
  fetchInvestigations,
  getInvestigationDetails,
  newInvestigation,
  updateInvestigations,
  archiveInvestigation,
  fetchPriorities,
  fetchStatuses,
  fetchUsers,
  fetchTeams,
  addNote,
  deleteNote,
  editNote,
  addEvidence,
  addPriority,
  updatePriority,
  reprioritizePriorities,
  deletePriority,
  addStatus,
  updateStatus,
  deleteStatus,
  removeEvidence,
  bulkArchiveInvestigation,
  deleteInvestigation,
  bulkDeleteInvestigations,
  setDefaultPriority,
  removeDefaultPriority,
  setDefaultStatus,
  removeDefaultStatus,
} from './api/investigationsAPI';
import type {
  InvestigationsDetailsAPIType,
  InvestigationsIndexAPIType,
  PriorityAPIType,
  StatusAPIType,
  UserAPIType,
  TeamAPIType,
  UpdateInvestigationResponseAPIType,
} from './api/investigationsAPI.types';

export type PaginatedProps = {
  page: number,
  perPage: number,
  query?: string,
  orderBy?: string,
  direction?: 'asc' | 'desc',
  filters?: ColumnFilterData,
};

export function useGetInvestigations({
  page,
  perPage,
  query,
  orderBy,
  direction,
  filters,
}: PaginatedProps, archived: boolean = false) {
  const { data, isLoading } = useQuery<InvestigationsIndexAPIType, Error>(
    ['get-investigations', page, perPage, query, orderBy, direction, filters, archived],
    () => fetchInvestigations(page, perPage, query, orderBy, direction, filters, archived),
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
    },
  );

  return {
    loadingInvestigations: isLoading,
    investigations: isLoading ? [] : data?.investigations,
    pagination: {
      page: data?.page || page,
      perPage: data?.per_page || perPage,
      total: data?.total || 0,
      grandTotal: data?.grand_total || 0,
      count: data?.count || 0,
    },
  };
}

export function useGetInvestigationDetails(invId: string) {
  const { data, isLoading } = useQuery<InvestigationsDetailsAPIType, Error>(
    ['get-investigation-details', invId],
    () => getInvestigationDetails(invId),
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      enabled: !!invId,
    },
  );

  return {
    loadingInvestigationDetails: isLoading,
    currentInvestigation: data,
  };
}

export function useNewInvestigation() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    newInvestigation,
    {
      onSuccess: () => {
        UserNotification.success('New Investigation created');
        queryClient.invalidateQueries(['get-investigations']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { createInvestigation: mutateAsync, creatingInvestigation: isLoading };
}

export function useUpdateInvestigations() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    updateInvestigations,
    {
      onSuccess: () => {
        UserNotification.success('Investigations updated successfully');
        queryClient.invalidateQueries(['get-investigation-details']);
        queryClient.invalidateQueries(['get-investigations']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    updateInvestigations: mutateAsync,
    updatingInvestigations: isLoading,
  };
}

export function useArchiveInvestigation() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    archiveInvestigation,
    {
      onSuccess: () => {
        UserNotification.success('Investigation archive status updated successfully');
        queryClient.invalidateQueries(['get-investigation-details']);
        queryClient.invalidateQueries(['get-investigations']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    archiveInvestigation: mutateAsync,
    archivingInvestigation: isLoading,
  };
}

export function useBulkArchiveInvestigation() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    bulkArchiveInvestigation,
    {
      onSuccess: () => {
        UserNotification.success('Investigations archive status updated successfully');
        queryClient.invalidateQueries(['get-investigation-details']);
        queryClient.invalidateQueries(['get-investigations']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    bulkArchiveInvestigation: mutateAsync,
    archivingInvestigations: isLoading,
  };
}

export function useDeleteInvestigation() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    deleteInvestigation,
    {
      onSuccess: () => {
        UserNotification.success('Investigation deleted successfully');
        queryClient.invalidateQueries(['get-investigations']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    deleteInvestigation: mutateAsync,
    deletingInvestigation: isLoading,
  };
}

export function useBulkDeleteInvestigations() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    bulkDeleteInvestigations,
    {
      onSuccess: () => {
        UserNotification.success('Investigations deleted successfully');
        queryClient.invalidateQueries(['get-investigations']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return {
    bulkDelete: mutateAsync,
    bulkDeleting: isLoading,
  };
}

export function useAddEvidence() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    addEvidence,
    {
      onSuccess: async (data: UpdateInvestigationResponseAPIType) => {
        if (data.errors.length > 0) {
          const error = { type: 'warning', message: `${data.errors}` };
          UserNotification[error.type](error.message);
        } else {
          UserNotification.success('Evidence added to investigation');
        }

        await new Promise((resolve: (arg?: any) => void) => {
          setTimeout(() => {
            queryClient.invalidateQueries(['get-investigation-details']);
            resolve();
          }, 1000);
        });
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { addEvidence: mutateAsync, addingEvidence: isLoading };
}

export function useRemoveEvidence() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation(
    removeEvidence,
    {
      onSuccess: async () => {
        UserNotification.success('Evidence removed from investigation');

        await new Promise((resolve: (arg?: any) => void) => {
          setTimeout(() => {
            queryClient.invalidateQueries(['get-investigation-details']);
            resolve();
          }, 1000);
        });
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { removeEvidence: mutateAsync };
}

export function useAddNote() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    addNote,
    {
      onSuccess: () => {
        UserNotification.success('New note added to investigation');
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { addNote: mutateAsync, addingNote: isLoading };
}

export function useEditNote() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    editNote,
    {
      onSuccess: () => {
        UserNotification.success('Note edited');
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { editNote: mutateAsync, editingNote: isLoading };
}

export function useDeleteNote() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation(
    deleteNote,
    {
      onSuccess: () => {
        UserNotification.success('Note deleted from investigation');
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { deleteNote: mutateAsync };
}

export function useGetPriorities(execute: boolean = true) {
  const { data, isLoading } = useQuery<PriorityAPIType[], Error>(
    ['get-all-priorities'],
    fetchPriorities,
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      enabled: execute,
    },
  );

  return {
    priorities: data || [],
    loadingPriorities: isLoading,
  };
}

export function useGetStatuses(execute: boolean = true) {
  const { data, isLoading } = useQuery<StatusAPIType[], Error>(
    ['get-all-statuses'],
    fetchStatuses,
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      enabled: execute,
    },
  );

  return {
    statuses: data || [],
    loadingStatuses: isLoading,
  };
}

export function useGetUsers(execute: boolean = true) {
  const { data, isLoading } = useQuery<{ users: UserAPIType[] }, Error>(
    ['get-all-users'],
    fetchUsers,
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      enabled: execute,
    },
  );

  const { users } = data || { users: [] };

  return {
    users: users,
    loadingUsers: isLoading,
  };
}

export function useGetTeams(execute: boolean = true) {
  const { data, isLoading } = useQuery<{ teams: TeamAPIType[] }, Error>(
    ['get-all-teams'],
    fetchTeams,
    {
      onError: (error: Error) => UserNotification.error(error.message),
      retry: 2,
      enabled: execute,
    },
  );

  const { teams } = data || { teams: [] };

  return {
    teams,
    loadingTeams: isLoading,
  };
}

export function useAddPriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    addPriority,
    {
      onSuccess: () => {
        UserNotification.success('New priority tag added');
        queryClient.invalidateQueries(['get-all-priorities']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { addPriority: mutateAsync, addingPriority: isLoading };
}

export function useUpdatePriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    updatePriority,
    {
      onSuccess: () => {
        UserNotification.success('Priority tag updated');
        queryClient.invalidateQueries(['get-all-priorities']);
        queryClient.invalidateQueries(['get-investigations']);
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { updatePriority: mutateAsync, updatingPriority: isLoading };
}

export function useReprioritizePriorities() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    reprioritizePriorities,
    {
      onSuccess: () => {
        UserNotification.success('Priority tags updated');
        queryClient.invalidateQueries(['get-all-priorities']);
        queryClient.invalidateQueries(['get-investigations']);
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { reprioritizePriorities: mutateAsync, reprioritizingPriorities: isLoading };
}

export function useDeletePriority() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation(
    deletePriority,
    {
      onSuccess: () => {
        UserNotification.success('Priority tag deleted');
        queryClient.invalidateQueries(['get-all-priorities']);
        queryClient.invalidateQueries(['get-investigations']);
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { deletePriority: mutateAsync };
}

export function useAddStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    addStatus,
    {
      onSuccess: () => {
        UserNotification.success('New status tag added');
        queryClient.invalidateQueries(['get-all-statuses']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { addStatus: mutateAsync, addingStatus: isLoading };
}

export function useUpdateStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    updateStatus,
    {
      onSuccess: () => {
        UserNotification.success('Status tag updated');
        queryClient.invalidateQueries(['get-all-statuses']);
        queryClient.invalidateQueries(['get-investigations']);
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { updateStatus: mutateAsync, updatingStatus: isLoading };
}

export function useDeleteStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation(
    deleteStatus,
    {
      onSuccess: () => {
        UserNotification.success('Status tag deleted');
        queryClient.invalidateQueries(['get-all-statuses']);
        queryClient.invalidateQueries(['get-investigations']);
        queryClient.invalidateQueries(['get-investigation-details']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { deleteStatus: mutateAsync };
}

export function useSetDefaultPriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    setDefaultPriority,
    {
      onSuccess: () => {
        UserNotification.success('Default priority set');
        queryClient.invalidateQueries(['get-all-priorities']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { setDefault: mutateAsync, settingDefault: isLoading };
}

export function useRemoveDefaultPriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    removeDefaultPriority,
    {
      onSuccess: () => {
        UserNotification.success('Default priority removed');
        queryClient.invalidateQueries(['get-all-priorities']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { removeDefault: mutateAsync, removingDefault: isLoading };
}

export function useSetDefaultStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    setDefaultStatus,
    {
      onSuccess: () => {
        UserNotification.success('Default status set');
        queryClient.invalidateQueries(['get-all-statuses']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { setDefault: mutateAsync, settingDefault: isLoading };
}

export function useRemoveDefaultStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(
    removeDefaultStatus,
    {
      onSuccess: () => {
        UserNotification.success('Default status removed');
        queryClient.invalidateQueries(['get-all-statuses']);
      },
      onError: (error: Error) => UserNotification.error(error.message),
    },
  );

  return { removeDefault: mutateAsync, removingDefault: isLoading };
}
